import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import { jwtDecode } from 'jwt-decode'; 
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';  // Import js-cookie to handle cookies
import { AuthContext } from './AuthContext';  // Import AuthContext
import { message } from 'antd';


function ServerStatus({ showOnlyBackend }) {
  const [serverStatus, setServerStatus] = useState('Checking...');
  const [isServerUp, setIsServerUp] = useState(null);
  const [backendStatus, setBackendStatus] = useState('Checking...');
  const [localStatus, setLocalStatus] = useState('Checking...');
  const [serverWorker, setServerWorker] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { setIsAuthenticated, setRole } = useContext(AuthContext);  // Get methods from AuthContext

  // Function to check if current page is public
  const isPublicPage = () => {
    const publicPages = ['/esd-check', '/developer/feedback'];
    return publicPages.includes(location.pathname);
  };

  // Function to log out the user and remove token and role
  const logout = () => {
    // Don't logout if user is on a public page
    if (isPublicPage()) {
      console.log('Skipping logout on public page:', location.pathname);
      return;
    }
    
    Cookies.remove('token');
    Cookies.remove('role');
    setIsAuthenticated(false);  // Update state in AuthContext
    setRole(null);  // Remove role from AuthContext
    navigate('/login');  // Navigate to login page
  };

  // Function to fetch workers and find webork server
  const fetchWorkers = async () => {
    try {
      const response = await fetch('https://scripthub.serviceorderhub.com/dashboardapi/workers/listsecure');
      if (response.ok) {
        const data = await response.json();
        // Find worker with type "webork" instead of "local_backend"
        const serverWorkerData = data.workers.find(worker => worker.worker_type === "webork");
        
        if (serverWorkerData) {
          console.log('Found webork worker:', serverWorkerData);
          setServerWorker(serverWorkerData);
        } else {
          console.error('No webork worker found in the workers list');
        }
      } else {
        console.error('Failed to fetch workers list');
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  };

  // Function to check server status via a ping to the backend
  const checkServerStatus = async (manualSource = null) => {
    // Only set "Checking..." for the clicked source
    if (manualSource === "backend") {
      setBackendStatus('Checking...');
    } else if (manualSource === "local") {
      setLocalStatus('Checking...');
    }
    
    try {
      const dashboardapi = process.env.REACT_APP_STATUS_API_URL;
      
      // Prioritize serverWorker IP if available, otherwise fallback to localStorage
      const localIp = serverWorker?.worker_local_ip || localStorage.getItem('serverIp');
      
      const [backendResult, localResult] = await Promise.allSettled([
        fetch(`${dashboardapi}/ping`),
        localIp ? fetch(`http://${localIp}:5050/api/health`) : Promise.reject('No WebOrk server IP available')
      ]);
      
      const backendOk = backendResult.status === 'fulfilled' && backendResult.value.ok;
      const localOk   = localResult.status === 'fulfilled' && localResult.value.ok;
      
      // Removed setTimeout delay or reduced to minimal value
      setBackendStatus(backendOk ? 'Yes' : 'No');
      setLocalStatus(localOk ? 'Yes' : 'No');
      setIsServerUp(backendOk && localOk);
    } catch (error) {
      console.error('Error checking server status:', error);
      setBackendStatus('No');
      setLocalStatus('No');
      setIsServerUp(false);
    }
  };

  // Function to check if the token is expired
  const isTokenExpired = (token) => {
    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;  // Current time in seconds
      return decodedToken.exp < currentTime;  // Return true if the token is expired
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;  // If there is an error, assume the token is expired
    }
  };

  // Function to check if the user is frozen
  const checkIfUserIsFrozen = async () => {
    // Don't check freeze status on public pages
    if (isPublicPage()) {
      return;
    }
    
    const apiUrl = process.env.REACT_APP_STATUS_API_URL;
    const token = Cookies.get('token'); // Get token from cookies

    if (!token) {
        console.error('No token found');
        // Only logout if not on public page
        if (!isPublicPage()) {
          logout();
        }
        return;
    }

    // Check if the token is expired
    if (isTokenExpired(token)) {
        // Only show error message and logout if not on public page
        if (!isPublicPage()) {
          message.error({
            content: 'Session expired, please log in again.',
            duration: 0, // 0 means the message will stay until manually closed
          });
          logout(); // Log out if the token is expired
        }
        return;
    }

    try {
        const decodedToken = jwtDecode(token);
        const username = decodedToken.sub; // Get username (sub) from the token

        const response = await fetch(`${apiUrl}/check_freeze`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ username }),
        });

        if (response.ok) {
            const data = await response.json();
            if (data.status === "success" && data.user && data.user.freeze === true) {
                // Show alert first, then log out
                alert('Account has been frozen by admin');

                // After alert, perform logout
                setTimeout(() => {
                    logout(); // Log out the user after alert
                }, 100); // Small delay before logging out
            } else {
                console.log(`User ${username} is not frozen.`);
            }
        } else {
            console.error('Something went wrong while checking freeze status:', response.status);
        }
    } catch (error) {
        console.error('Error checking freeze status:', error);
    }
};


  // Use useEffect to check server status, freeze status, and token expiration when the component loads
  useEffect(() => {
    // Fetch workers list first
    fetchWorkers();
    
    // Immediate first check for faster initialization
    checkServerStatus();
    
    // Perform another check soon after initial load to update any "Checking..." status quickly
    const initialCheck = setTimeout(() => {
      checkServerStatus();
    }, 500);
    
    const interval = setInterval(() => {
      checkServerStatus(false);
      checkIfUserIsFrozen();
    }, 10000);  // Check every 10 seconds

    // Clean up the interval when the component unmounts
    return () => {
      clearInterval(interval);
      clearTimeout(initialCheck);
    };
  }, []);

  // New event handlers for each status
  const handleBackendClick = () => {
    checkServerStatus("backend");
  };

  const handleLocalClick = () => {
    // Refresh worker list when manually checking local status
    fetchWorkers().then(() => checkServerStatus("local"));
  };

  // Return UI with separate hover effects for Backend and Local status on the same line
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
      {!showOnlyBackend ? (
        <>
          <Box
            component="span"
            onClick={handleBackendClick}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            <span style={{ color: 'rgba(135,135,135,0.6)', fontWeight: 'normal' }}>Backend: </span> 
            <span style={{ color: 'rgba(135,135,135,0.6)', fontWeight: 'normal' }}>
              {backendStatus}
            </span>
          </Box>
          <Box component="span" sx={{ mx: 1 }}>
            |
          </Box>
          <Box
            component="span"
            onClick={handleLocalClick}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            <span style={{ color: 'rgba(135,135,135,0.6)', fontWeight: 'normal' }}>WebOrk: </span> 
            <span style={{ color: 'rgba(135,135,135,0.6)', fontWeight: 'normal' }}>
              {localStatus}
            </span>
          </Box>
        </>
      ) : (
        <Box
          component="span"
          onClick={handleBackendClick}
          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          <span style={{ color: 'rgba(135,135,135,0.6)', fontWeight: 'normal' }}>Backend: </span>
          <span style={{ color: 'rgba(135,135,135,0.6)', fontWeight: 'normal' }}>
            {backendStatus}
          </span>
        </Box>
      )}
    </Box>
  );
}

ServerStatus.defaultProps = {
  showOnlyBackend: false
};

export default ServerStatus;
